import React, { useState } from "react";

const Projects = () => {
  const majorProjects = [
    {
      id: 1,
      title: "Online Video KYC",
      description:
        "A self-sufficient online KYC tool where users can register, authenticate and complete their KYC. Includes features like facial authentication, document capturing, and verification.",
      image: "/images/projects/base-kyc.png",
      fallbackImage: "https://placehold.co/600x400/e2e8f0/475569?text=BaseKYC",
      techStack: ["Node.js", "Express", "PostgreSQL", "EJS", "Bcrypt"],
      codeUrl: "https://github.com/dhruvatri/baseKYC",
      hosted: false,
    },
    {
      id: 2,
      title: "DevCoder",
      description:
        "An online coding practice platform with structured learning paths, coding problems, and profile management for users to track their progress.",
      image: "/images/projects/dev-coder.png",
      fallbackImage: "https://placehold.co/600x400/e2e8f0/475569?text=DevCoder",
      techStack: ["TypeScript", "React", "CSS"],
      codeUrl: "https://github.com/dhruvatri/devcoder",
      hosted: false,
    },
    {
      id: 3,
      title: "YouTube Clone",
      description:
        "A YouTube clone application built with React and Material UI that replicates core features of the YouTube interface and functionality.",
      image: "/images/projects/youtube.png",
      fallbackImage:
        "https://placehold.co/600x400/e2e8f0/475569?text=YouTube+Clone",
      techStack: ["React", "Material UI", "JavaScript", "CSS"],
      demoUrl: "https://inquisitive-heliotrope-1e5567.netlify.app/",
      codeUrl: "https://github.com/dhruvatri/Youtube_Clone",
      hosted: true,
    },
  ];

  const funProjects = [
    {
      id: 4,
      title: "Advanced Tic Tac Toe",
      description:
        "Advanced Tic Tac Toe with 9X9 board and user score tracking system implemented with React.js",
      image: "/images/projects/tic-tac-toe.png",
      fallbackImage:
        "https://placehold.co/600x400/e2e8f0/475569?text=Advanced+TicTacToe",
      techStack: ["React", "JavaScript", "CSS", "LocalStorage"],
      demoUrl: "https://advanced-tic-tac-toe-react.vercel.app/",
      codeUrl: "https://github.com/dhruvatri/Advanced-TicTacToe-React",
      hosted: true,
    },
    {
      id: 5,
      title: "Weather App",
      description:
        "Weather application with current location detection and location search suggestions to check weather conditions worldwide.",
      image: "/images/projects/weather-app.png",
      fallbackImage:
        "https://placehold.co/600x400/e2e8f0/475569?text=Weather+App",
      techStack: ["React", "JavaScript", "OpenWeather API", "CSS"],
      demoUrl: "https://weather-app-react-kappa-eight.vercel.app",
      codeUrl: "https://github.com/dhruvatri/Weather-App-react",
      hosted: true,
    },
    {
      id: 6,
      title: "To-Do List",
      description:
        "Task management application with features to add tasks, mark completed, delete individual or all completed tasks at once.",
      image: "/images/projects/to-do.png",
      fallbackImage:
        "https://placehold.co/600x400/e2e8f0/475569?text=Todo+List",
      techStack: ["React", "JavaScript", "CSS", "LocalStorage"],
      demoUrl: "https://to-do-list-react-azure-two.vercel.app",
      codeUrl: "https://github.com/dhruvatri/To-Do-List-React",
      hosted: true,
    },
    {
      id: 7,
      title: "Drum Kit Game",
      description:
        "Interactive drum kit simulator where users can play drum sounds using keyboard keys or by clicking on the drum elements.",
      image: "/images/projects/drum-kit.png",
      fallbackImage:
        "https://placehold.co/600x400/e2e8f0/475569?text=Drum+Kit+Game",
      techStack: ["JavaScript", "HTML", "CSS", "Web Audio API"],
      demoUrl: "https://drum-kit-game-sigma.vercel.app",
      codeUrl: "https://github.com/dhruvatri/Drum-Kit-Game",
      hosted: true,
    },
    {
      id: 8,
      title: "Movable Calculator",
      description:
        "A draggable calculator interface that can be moved around the screen while performing basic mathematical operations.",
      image: "/images/projects/calculator.png",
      fallbackImage:
        "https://placehold.co/600x400/e2e8f0/475569?text=Movable+Calculator",
      techStack: ["JavaScript", "TypeScript", "HTML", "CSS"],
      demoUrl: "https://movable-calculator.vercel.app",
      codeUrl: "https://github.com/dhruvatri/MovableCalculator",
      hosted: true,
    },
  ];

  const ProjectCard = ({ project }) => {
    const [imgError, setImgError] = useState(false);

    return (
      <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col">
        <div className="relative h-56 overflow-hidden">
          <img
            src={imgError ? project.fallbackImage : project.image}
            alt={project.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        </div>
        <div className="p-5 flex-grow flex flex-col">
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            {project.title}
          </h3>
          <p className="text-gray-600 mb-4 flex-grow">{project.description}</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {project.techStack.map((tech, index) => (
              <span
                key={index}
                className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded"
              >
                {tech}
              </span>
            ))}
          </div>
          <div className="flex space-x-2 mt-auto">
            <a
              href={project.codeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`${
                project.hosted ? "flex-1" : "w-full"
              } bg-gray-700 hover:bg-gray-800 text-white font-bold py-2 px-4 rounded transition duration-300 text-center`}
            >
              GitHub
            </a>
            {project.hosted && (
              <a
                href={project.demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-300 text-center"
              >
                Live Demo
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="projects" className="py-4 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Projects
        </h2>

        <div className="mb-8">
          <h3 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-2">
            Major Projects
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {majorProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        </div>

        {/* Fun Projects Section */}
        <div>
          <h3 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-2">
            Fun Projects
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {funProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
